import React, { useContext } from 'react';
import { isBrowser, isMobile } from 'react-device-detect';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { Floor, Room, RoomPlan } from 'models';
import { WindowDimentions, FloorPlanDimensionsData } from 'types';
import { AppContext } from 'contexts';
import { useWindowDimensions } from 'hooks';
import { images } from 'images';
import ReactPixel from 'react-facebook-pixel';

const useStyles = makeStyles({
  floorPlanContainer: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    zIndex: 1000,
    '&.mobile': {},
    '&.desktop': {
      flexDirection: 'column',
    },
  },
  floorPlanBox: {
    width: 'fit-content',
    padding: 15,
    background: 'rgba(0, 0, 0, 0.3)',
  },
  floorPlan: {
    position: 'relative',
  },
  toggleZoom: {
    position: 'absolute',
    top: -10,
    right: -10,
    width: 15,
    height: 15,
    color: 'white',
    fontSize: 24,
    cursor: 'pointer',
  },
  roomBox: {
    position: 'absolute',
    display: 'flex',
    boxSizing: 'border-box',
  },
  room: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"Ford Antenna", sans-serif',
    cursor: 'pointer',
    pointerEvents: 'auto',
    margin: 1,

    '& img': {
      width: '20px',
      height: '20px',
    },
  },
  floorsBox: {
    display: 'flex',
    boxSizing: 'border-box',

    '&.desktop': {
      height: 40,
      maxWidth: 'calc(100vh - 100px)',
      background: 'rgba(0, 0, 0, 0.3)',
      padding: '5px 0 5px 10px',
    },

    '&.mobile': {
      width: 100,
      flexDirection: 'column',
      justifyContent: 'flex-end',
      marginLeft: 10,
      marginRight: 10,
    },
  },
  floorItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    textAlign: 'center',
    overflow: 'hidden',
    boxSizing: 'border-box',
    cursor: 'pointer',

    '&.mobile': {
      width: '100%',
      height: 40,
      borderRadius: '8px',
      background: 'rgba(0, 0, 0, 0.3)',
      padding: '3px',
      margin: '5px 0',

      '&.selected': {
        background: 'rgba(0, 0, 0, 0.8)',
      },
    },

    '&.desktop': {
      position: 'relative',
      margin: '0 5px',

      '&.selected::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        bottom: 0,
        width: '100%',
        height: 5,
        background: '#00bcf2',
      },
    },
  },
});

function FloorPlan() {
  const classes = useStyles();
  const appContext = useContext(AppContext);
  const windowDimensions: WindowDimentions = useWindowDimensions();

  const {
    building,
    currentRoom,
    currentFloor,
    floorPlanZoom,
    floorPlanState,
    getRoomDimensions,
    setFloorPlanZoom,
    setCurrentRoom,
    setCurrentFloor,
  } = appContext;

  if (!floorPlanState) return null;

  const rooms =
    building && building.floorPlanRooms
      ? building.floorPlanRooms[currentFloor].rooms
      : [];

  const floors = building ? building.getFloors(currentFloor) : [];
  const floorPlanDimensions: FloorPlanDimensionsData = Floor.getPlanDimensions(
    windowDimensions.width,
    windowDimensions.height,
  );

  const roomColor = 'white';
  const badMarkerColor = 'silver';
  const zoomImage = floorPlanZoom ? 'zoomDown' : 'zoomUp';

  const roomPlans: RoomPlan[] = rooms.map((room: Room) => {
    const floorPlanSize = {
      width: floorPlanDimensions[floorPlanZoom ? 'max_width' : 'min_width'],
      height: floorPlanDimensions[floorPlanZoom ? 'max_height' : 'min_height'],
    };

    const data = getRoomDimensions(room, floorPlanSize);

    let cursor = 'pointer';
    let textColor = 'black';
    let bgColor = roomColor;

    if (!room.valideMarker || !room.markers.length) {
      cursor = 'default';
      bgColor = badMarkerColor;
    }
    if (room.rejected === true) {
      bgColor =
        'linear-gradient(176deg, rgba(248, 178, 166,0.7) 0%, rgba(244, 39, 16,0.7) 41%, rgba(245, 44, 18,0.7) 100%)';
    }

    const letterToWidthRatio = 0.5476;
    let fontSize = data.roomWidth / (room.roomname.length * letterToWidthRatio) - 3;
    if (fontSize > 20) {
      fontSize = 20;
    }

    let icon = null;
    let displayName = room.roomname;
    if (fontSize < 10) {
      displayName = '';
      if (data.roomWidth > 15 && data.roomHeight > 15) {
        icon = Room.getIcon(room.roomname);
      }
    }

    if (currentRoom && room.id === currentRoom.id) {
      bgColor = '#00bcf2';
      textColor = 'white';
    }

    return {
      ...data,
      room,
      icon,
      displayName,
      fontSize,
      textColor,
      background: bgColor,
      cursor,
    };
  });

  const onRoomClick = (room: Room) => {
    return () => {
      if (!floorPlanZoom) {
        setFloorPlanZoom(true);
        return;
      }
      setCurrentRoom(room);
    };
  };

  const onToggleZoom = () => {
    setFloorPlanZoom(!floorPlanZoom);
  };

  const onFloorClick = (floorId: number) => {
    return () => {
      ReactPixel.track('ViewContent', {
        content_name: 'View Floor',
        value: floorId,
      });
      setCurrentFloor(Number(floorId));
    };
  };

  const floorPlanStyle = {
    width: floorPlanDimensions[floorPlanZoom ? 'max_width' : 'min_width'],
    height: floorPlanDimensions[floorPlanZoom ? 'max_height' : 'min_height'],
  };

  const devices = [{ mobile: isMobile }, { desktop: isBrowser }];

  return (
    <div className={classNames(classes.floorPlanContainer, ...devices)}>
      <div className={classes.floorPlanBox}>
        <div className={classes.floorPlan} style={floorPlanStyle}>
          <img
            className={classes.toggleZoom}
            src={images[zoomImage]}
            alt='Toggle zoom'
            onClick={onToggleZoom}
            onTouchEnd={onToggleZoom}
          />
          {roomPlans.map((m: RoomPlan) => {
            const roomBoxStyle = {
              top: m.topMargin,
              left: m.leftMargin,
              width: m.roomWidth,
              height: m.roomHeight,
            };

            const roomStyle = {
              color: m.textColor,
              fontSize: m.fontSize,
              background: m.background,
              cursor: m.cursor,
            };

            const roomItem = m.icon ? (
              <img src={`/images/icons/${m.icon}.png`} alt='any' />
            ) : (
              m.displayName
            );

            return (
              <div
                key={m.room.id}
                className={classes.roomBox}
                style={roomBoxStyle}
                onClick={onRoomClick(m.room)}
                onTouchEnd={onRoomClick(m.room)}
              >
                <div className={classes.room} style={roomStyle}>
                  {floorPlanZoom && roomItem}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {floorPlanZoom && (
        <div className={classNames(classes.floorsBox, ...devices)}>
          {floors.map((floor: Floor) => {
            return (
              <div
                key={floor.id}
                className={classNames(classes.floorItem, ...devices, {
                  selected: floor.selected,
                })}
                onClick={onFloorClick(floor.id)}
                onTouchEnd={onFloorClick(floor.id)}
              >
                {floor.name}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default FloorPlan;
